/**
 * Quick-ship RMA processing form.
 *
 * Staff-only form accessed from ReturnView when a quick-ship RMA is in a
 * shippable state. Sends the replacement radio and shipping details to
 * /rmas/:id/sendquickship/. The parent ReturnView owns page chrome
 * (AppNav / TitleBar); this component renders only the form body. On
 * success it navigates to /rma/:id/view.
 *
 * Contains:
 * - QuickShipReturnForm — default export, the form component
 */

import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { List, Map } from 'immutable';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { ContactSelector } from '@sophocles/components/contacts';
import RadioValidationBox from '@sophocles/components/RadioValidationBox';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { useSendQuickShipRmaMutation } from '@sophocles/features/entities/rmas';
import { addToast } from '@sophocles/features/toast';
import { formatUserString } from '@sophocles/utils';
import { parseApiError } from '@sophocles/utils/apiErrors';
import SpecialInstructionsCollapse from '@sophocles/views/returns/components/CustomerSpecialInstructionsCollapse';
import SiteGroupInstructionsCollapse from '@sophocles/views/returns/components/SiteGroupInstructionsCollapse';
import TextDisplay from '@sophocles/views/returns/components/TextDisplay';

import { SectionCard } from '../components/ReturnSection';
import { isValidContactId, shipContactFields } from '../utils/contactPayload';
import { buildContactFromRma } from '../utils/contactSeeding';
import { returnEntityLabels } from '../utils/tenantLabels';

import InventoryQtySelect from './fields/InventoryQtySelect';
import RecentTrackingRmaSelect from './fields/RecentTrackingRmaSelect';
import ShippingModeSelect from './fields/ShippingModeSelect';
import { isValidShippingCost } from './validation';

// Filled multiline centers short text vertically in a tall slot (root
// align-items:center) and pads the root rather than the textarea — anchor
// content to the top instead.
const multilineFilledSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

// ─── helpers ──────────────────────────────────────────────────────────────────

/**
 * Format an ISO date string for display.
 * @param {string|null} iso
 * @returns {string|null}
 */
function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

/**
 * Format an ISO datetime string for display.
 * @param {string|null} iso
 * @returns {string|null}
 */
function fmtDateTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()}, ${d.toLocaleTimeString()}`;
  } catch {
    return iso;
  }
}

/**
 * Append shipping-contact inline fields to the payload object.
 * Mutates payload in place; only called when selectedShippingContact is set.
 *
 * @param {object} payload
 * @param {object} contact
 * @param {import('immutable').Map} rma
 */
function buildShipContactPayload(payload, contact, rma) {
  const siteGroupName = rma.getIn(['site_group', 'name'], '');
  const customerName = rma.getIn(['site_group', 'customer', 'name'], '');

  Object.assign(payload, shipContactFields(contact, { siteGroupName, customerName }));
  payload.shipping_contact = contact.id && isValidContactId(contact.id) && !contact._isAddress ? contact.id : null;
}

// ─── component ────────────────────────────────────────────────────────────────

/**
 * @param {{ rma: import('immutable').Map }} props
 */
export default function QuickShipReturnForm({ rma }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(getAuthUserSelector);

  const [sendQuickShipRma] = useSendQuickShipRmaMutation();

  // ── Derived read-only values from RMA ─────────────────────────────────────

  const rmaId = rma.get('id', null);
  const rmaNumber = rma.get('rma_number', null);
  const rmaStatusDisplay = rma.get('status_display', '');

  const customer = rma.getIn(['site_group', 'customer'], Map());
  const customerId = customer ? customer.get('id', 0) : 0;
  const entityLabels = returnEntityLabels(t, customer);
  const siteGroup = rma.get('site_group', Map());
  const siteGroupId = siteGroup ? siteGroup.get('id', 0) : 0;
  const site = rma.get('site', null);
  const ticket = rma.get('ticket', null);

  const legacyOldIccid = rma.get('legacy_incoming_iccid', null);
  const legacyNewIccid = rma.get('legacy_new_iccid', null);
  const legacyReplacedIccid = rma.get('legacy_replaced_iccid', null);

  const returnedRadio = rma.get('radio_returned_by_user', null);
  const oldICCID = rma.getIn(['radio_returned_by_user', 'iccid'], null);

  const shippingCostIncludedOnRma = rma.get('shipping_cost_included_on_rma', null);
  const pastInventoryAdjustments = rma.get('inventory_adjustments', List());

  const supportContactName = rma.get('support_contact_name', '');
  const supportContactEmail = rma.get('support_contact_email', '');
  const supportContactPhone = rma.get('support_contact_phone', '');
  const legacyContact = rma.get('contact', null);
  const legacyContactName = legacyContact ? legacyContact.get('name', null) : null;
  const legacyContactEmail = legacyContact ? legacyContact.get('email', null) : null;
  const legacyContactTitle = legacyContact ? legacyContact.get('title', null) : null;
  const hasReportingContact = !!(supportContactName || supportContactEmail);
  const hasLegacyContact = !!(legacyContactName || legacyContactEmail);

  const issueTime = rma.get('input_time', null);
  const updateTime = rma.get('update_time', null);
  const shippingDate = rma.get('shipping_date', null);
  const receivedDate = rma.get('received_date', null);
  const invoiceDate = rma.get('invoiced_time', null);
  const issueUser = rma.get('input_user', null);
  const updateUser = rma.get('update_user', null);
  const shippingUser = rma.get('shipping_user', null);
  const receivedUser = rma.get('received_user', null);
  const invoiceUser = rma.get('invoiced_user', null);

  const isStaff = user.get('is_staff', false);

  // ── Formik initialValues ──────────────────────────────────────────────────

  const initialValues = {
    comments: rma.get('comments', '') || '',
    hidden_comments: rma.get('hidden_comments', '') || '',
    tracking_number: rma.get('tracking_number', '') || '',
    shipping_cost: rma.get('shipping_cost', '') || '',
    outbound_shipping_mode_id: rma.getIn(['outbound_shipping_mode', 'id'], null),
  };

  // ── External-component state ──────────────────────────────────────────────

  const [loading, setLoading] = useState(false);

  const [newRadio, setNewRadio] = useState(rma.get('radio_shipped_to_user', null));

  const [selectedShippingContact, setSelectedShippingContact] = useState(() => buildContactFromRma(rma, 'shipping'));

  const [shippingIncludedCheckbox, setShippingIncludedCheckbox] = useState(!!rma.get('shipping_cost_included_on_rma'));
  const [shippingCostIncludedOnRmaId, setShippingCostIncludedOnRmaId] = useState(
    shippingCostIncludedOnRma ? shippingCostIncludedOnRma.get('id', null) : null,
  );
  const [shippingCostError, setShippingCostError] = useState(null);

  const [removeFromInventory, setRemoveFromInventory] = useState(Map());
  const [packingSupplies, setPackingSupplies] = useState(Map());

  const [customerSpecialInstructionsFollowed, setCustomerSpecialInstructionsFollowed] = useState(true);
  const [siteGroupInstructionsFollowed, setSiteGroupInstructionsFollowed] = useState(true);

  const [unauthorized_swap, setUnauthorizedSwap] = useState(rma.get('unauthorized_swap', false));

  // ── Inventory handlers ────────────────────────────────────────────────────

  const setRemoveFromInventoryValue = useCallback(
    (id, value) => setRemoveFromInventory(prev => prev.set(id + '', value)),
    [],
  );

  const setPackingSuppliesValue = useCallback((id, value) => setPackingSupplies(prev => prev.set(id + '', value)), []);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    (values, { setSubmitting }) => {
      if (!rmaId) {
        setSubmitting(false);
        return;
      }

      if (!isValidShippingCost(values.shipping_cost)) {
        dispatch(
          addToast({
            message: t('rma.shippingCostInvalid', 'Shipping cost must be a valid dollar amount (e.g. 12.50).'),
            level: 'error',
          }),
        );
        setShippingCostError(t('rma.mustBeValidDollar', 'Must be a valid dollar amount (e.g. 12.50)'));
        setSubmitting(false);
        return;
      }

      const inventoryAdjustments = packingSupplies
        .map((qty, id) => ({ quantity_shipped: qty, product_id: id, packing_supply: true }))
        .toList()
        .concat(
          removeFromInventory
            .map((qty, id) => ({ quantity_shipped: qty, product_id: id, packing_supply: false }))
            .toList(),
        )
        .toArray();

      const payload = {
        comments: values.comments,
        hidden_comments: values.hidden_comments,
        unauthorized_swap,
        radio_shipped_to_user_id: newRadio ? newRadio.get('id', 0) : null,
        inventory_adjustments: inventoryAdjustments,
        shipping_address_id: null,
        outbound_shipping_mode_id: values.outbound_shipping_mode_id,
      };

      if (!shippingIncludedCheckbox) {
        payload.tracking_number = values.tracking_number;
        payload.shipping_cost = values.shipping_cost || null;
      } else {
        payload.shipping_cost_included_on_rma_id = shippingCostIncludedOnRmaId;
      }

      if (selectedShippingContact) {
        buildShipContactPayload(payload, selectedShippingContact, rma);
      }

      setLoading(true);
      sendQuickShipRma({ id: rmaId, body: payload })
        .unwrap()
        .then(() => {
          setLoading(false);
          setSubmitting(false);
          dispatch(
            addToast({
              title: t('general.success', 'Success'),
              message: t('rma.rmaShipped', 'RMA shipped'),
              level: 'success',
              dismissible: true,
            }),
          );
          navigate(`/rma/${rmaId}/view`, { replace: true });
        })
        .catch(err => {
          setLoading(false);
          setSubmitting(false);
          // Non-quick-ship guard returns HTTP 400 { detail }.
          const { nonFieldMessages } = parseApiError(err, {
            knownFields: [],
            genericMessage: t('rma.processError', 'Error processing RMA'),
          });
          dispatch(
            addToast({
              message: nonFieldMessages[0] ?? t('rma.processError', 'Error processing RMA'),
              level: 'error',
            }),
          );
        });
    },
    [
      rmaId,
      rma,
      unauthorized_swap,
      newRadio,
      shippingIncludedCheckbox,
      shippingCostIncludedOnRmaId,
      packingSupplies,
      removeFromInventory,
      selectedShippingContact,
      sendQuickShipRma,
      dispatch,
      navigate,
    ],
  );

  // ── canSubmit ─────────────────────────────────────────────────────────────

  const canSubmit =
    newRadio != null &&
    selectedShippingContact != null &&
    customerSpecialInstructionsFollowed &&
    siteGroupInstructionsFollowed &&
    !loading &&
    !shippingCostError;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', overflow: 'auto', flex: 1 }}>
      <Box sx={{ width: '100%' }}>
        {/* ── RMA Details ── */}
        <SectionCard
          title={t('rma.rmaDetailsTitle', 'RMA {{num}} Details', { num: rmaNumber ? '# ' + rmaNumber : '' })}
        >
          <Box component="dl" sx={{ m: 0 }}>
            <TextDisplay title={t('rma.rmaStatus', 'RMA Status')}>{rmaStatusDisplay}</TextDisplay>
            {!!customer && (
              <TextDisplay title={entityLabels.customer}>
                {customerId}. {customer.get('name', t('rma.noneLower', 'none'))}{' '}
                <Link style={{ fontSize: '13px' }} target="_blank" to={`/c/${customerId}/plants`}>
                  {t('rma.link', '(link)')}
                </Link>
              </TextDisplay>
            )}
            {!!siteGroup && (
              <TextDisplay title={entityLabels.siteGroup}>
                {siteGroup.get('name', '0')}. {siteGroup.get('description', t('rma.noneLower', 'none'))}{' '}
                <Link
                  style={{ fontSize: '13px' }}
                  target="_blank"
                  to={`/c/${customerId}/plant/${siteGroupId}/structures`}
                >
                  {t('rma.link', '(link)')}
                </Link>
              </TextDisplay>
            )}
            {!!site && (
              <TextDisplay title={entityLabels.structure}>
                {site.get('name', '0')}. {site.get('description', t('rma.noneLower', 'none'))}{' '}
                <Link
                  style={{ fontSize: '13px' }}
                  target="_blank"
                  to={`/c/${customerId}/plant/${siteGroupId}/structure/${site.get('id', 0)}`}
                >
                  {t('rma.link', '(link)')}
                </Link>
              </TextDisplay>
            )}
            {!!ticket && (
              <TextDisplay title={t('rma.supportTicket', 'Support Ticket')}>
                {ticket.get('ticket_number', '')}{' '}
                <Link style={{ fontSize: '13px' }} target="_blank" to={`/support/ticket/${ticket.get('id')}/view`}>
                  {t('rma.link', '(link)')}
                </Link>
              </TextDisplay>
            )}
            {issueTime && (
              <TextDisplay title={t('rma.timeIssuedFull', 'Time Issued')}>
                {fmtDateTime(issueTime)} {isStaff ? ' - ' + formatUserString(issueUser) : null}
              </TextDisplay>
            )}
            {shippingDate && (
              <TextDisplay title={t('rma.dateShippedFull', 'Date Shipped')}>
                {fmtDate(shippingDate)} {isStaff ? ' - ' + formatUserString(shippingUser) : null}
              </TextDisplay>
            )}
            {receivedDate && (
              <TextDisplay title={t('rma.dateProcessedFull', 'Date Processed')}>
                {fmtDate(receivedDate)} {isStaff ? ' - ' + formatUserString(receivedUser) : null}
              </TextDisplay>
            )}
            {invoiceDate && isStaff && (
              <TextDisplay title={t('rma.dateInvoicedStar', 'Date Invoiced*')}>
                {fmtDate(invoiceDate)} - {formatUserString(invoiceUser)}
              </TextDisplay>
            )}
            {updateTime && isStaff && (
              <TextDisplay title={t('rma.lastUpdatedStar', 'Last Updated*')}>
                {fmtDateTime(updateTime)} - {formatUserString(updateUser)}
              </TextDisplay>
            )}
          </Box>
        </SectionCard>

        {/* ── Reporting Contact (read-only display) ── */}
        <SectionCard title={t('rma.reportingContact', 'Reporting Contact')}>
          {!hasReportingContact && !hasLegacyContact && (
            <TextDisplay title={t('rma.contactLabel', 'Contact')}>
              {t('rma.noContactSelected', 'No Contact Selected')}
            </TextDisplay>
          )}
          {hasReportingContact && (
            <Box component="dl" sx={{ m: 0 }}>
              <TextDisplay title={t('general.name', 'Name')} show={!!supportContactName}>
                {supportContactName}
              </TextDisplay>
              <TextDisplay title={t('general.email', 'Email')} show={!!supportContactEmail}>
                {supportContactEmail}
              </TextDisplay>
              <TextDisplay title={t('general.phone', 'Phone')} show={!!supportContactPhone}>
                {supportContactPhone}
              </TextDisplay>
            </Box>
          )}
          {!hasReportingContact && hasLegacyContact && (
            <Box component="dl" sx={{ m: 0 }}>
              <TextDisplay title={t('general.name', 'Name')} show={!!legacyContactName}>
                {legacyContactName}
              </TextDisplay>
              <TextDisplay title={t('general.title', 'Title')} show={!!legacyContactTitle}>
                {legacyContactTitle}
              </TextDisplay>
              <TextDisplay title={t('general.email', 'Email')} show={!!legacyContactEmail}>
                {legacyContactEmail}
              </TextDisplay>
            </Box>
          )}
        </SectionCard>

        {/* ── Processing Details (read-only ICCIDs + special instructions) ── */}
        <SectionCard title={t('rma.processingDetails', 'Processing Details')}>
          <Box component="dl" sx={{ m: 0, mb: 2 }}>
            <TextDisplay title={t('rma.legacyOldIccid', 'Legacy Old ICCID')} show={!!legacyOldIccid}>
              {legacyOldIccid}
            </TextDisplay>
            <TextDisplay title={t('rma.legacyNewIccid', 'Legacy New ICCID')} show={!!legacyNewIccid}>
              {legacyNewIccid}
            </TextDisplay>
            <TextDisplay
              title={t('rma.legacyReplacementIccid', 'Legacy Replacement ICCID')}
              show={!!legacyReplacedIccid}
            >
              {legacyReplacedIccid}
            </TextDisplay>
            <TextDisplay title={t('rma.oldIccid', 'Old ICCID')} show={!!oldICCID}>
              {returnedRadio != null ? (
                <Link to={`/support/radios/${returnedRadio.get('id', 0)}`}>{oldICCID}</Link>
              ) : (
                oldICCID
              )}
            </TextDisplay>
          </Box>

          {customerId !== 0 && (
            <SpecialInstructionsCollapse
              customer={customerId}
              types={[1, 3]}
              checked={customerSpecialInstructionsFollowed}
              onToggle={() => setCustomerSpecialInstructionsFollowed(prev => !prev)}
            />
          )}
          {siteGroupId !== 0 && (
            <SiteGroupInstructionsCollapse
              siteGroup={siteGroupId}
              types={[1, 3]}
              checked={siteGroupInstructionsFollowed}
              onToggle={() => setSiteGroupInstructionsFollowed(prev => !prev)}
            />
          )}
        </SectionCard>

        <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
          {({ values, setFieldValue, submitForm, isSubmitting }) => (
            <Form>
              {/* ── Replacement Device ── */}
              <SectionCard title={t('rma.replacementDevice', 'Replacement Device')}>
                <RadioValidationBox
                  name="new_radio"
                  title={t('rma.rmaReplacementDevice', 'RMA Replacement Device')}
                  radio={newRadio}
                  onRadioSelect={setNewRadio}
                />
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    label={t('rma.unauthorizedSwap', 'Unauthorized Swap')}
                    control={
                      <Checkbox checked={unauthorized_swap} onChange={() => setUnauthorizedSwap(prev => !prev)} />
                    }
                  />
                </Box>
              </SectionCard>

              {/* ── Shipping ── */}
              <SectionCard title={t('rma.shipping', 'Shipping')}>
                {customerId > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <ContactSelector
                      value={selectedShippingContact}
                      onChange={setSelectedShippingContact}
                      customerId={customerId}
                      siteGroupId={siteGroupId}
                      customer={customer}
                      siteGroup={siteGroup}
                      label={t('rma.shippingContact', 'Shipping Contact')}
                      addressType="shipping"
                      required={true}
                      requiredFields={['address', 'name']}
                      primaryContext="rma"
                      contextLabel={t('rma.rma', 'RMA')}
                    />
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    label={t('rma.trackingShippingIncludedOther', 'Tracking # and shipping cost included on other RMA')}
                    control={
                      <Checkbox
                        checked={shippingIncludedCheckbox}
                        onChange={() => setShippingIncludedCheckbox(prev => !prev)}
                      />
                    }
                  />
                </Box>

                {!shippingIncludedCheckbox ? (
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {t('rma.trackingNumber', 'Tracking Number')}
                      </Typography>
                      <Field name="tracking_number">
                        {({ field }) => (
                          <TextField {...field} size="small" inputProps={{ maxLength: 50 }} sx={{ width: 256 }} />
                        )}
                      </Field>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {t('rma.shippingCost', 'Shipping Cost')}
                      </Typography>
                      <Field name="shipping_cost">
                        {({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            onChange={e => {
                              field.onChange(e);
                              const val = e.target.value;
                              if (!isValidShippingCost(val)) {
                                setShippingCostError(
                                  t('rma.mustBeValidDollar', 'Must be a valid dollar amount (e.g. 12.50)'),
                                );
                              } else {
                                setShippingCostError(null);
                              }
                            }}
                            error={!!shippingCostError}
                            helperText={shippingCostError}
                            inputProps={{ maxLength: 10 }}
                            sx={{ width: 256 }}
                          />
                        )}
                      </Field>
                    </Box>
                  </Stack>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    <RecentTrackingRmaSelect
                      siteGroupId={siteGroupId}
                      value={shippingCostIncludedOnRmaId}
                      onChange={setShippingCostIncludedOnRmaId}
                      selectedRma={shippingCostIncludedOnRma}
                    />
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <ShippingModeSelect
                      value={values.outbound_shipping_mode_id}
                      onChange={v => setFieldValue('outbound_shipping_mode_id', v)}
                      label={t('rma.outboundShippingInstructions', 'Outbound Shipping Instructions')}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* ── Inventory ── */}
              <SectionCard title={t('rma.inventory', 'Inventory')}>
                {!pastInventoryAdjustments.isEmpty() && (
                  <Box sx={{ mb: 2, fontSize: '14px' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {t('rma.previousInventoryAdjustments', 'Previous Inventory Adjustments')}
                      </Typography>
                      <Tooltip
                        title={t(
                          'rma.inventoryAdjustmentsTooltip',
                          'These inventory adjustments have already been made and cannot be reverted.',
                        )}
                        placement="top"
                      >
                        <Box component="span" sx={{ color: 'text.secondary', fontSize: '14px', cursor: 'default' }}>
                          ⓘ
                        </Box>
                      </Tooltip>
                    </Stack>
                    <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                      {pastInventoryAdjustments.map((item, index) => (
                        <li key={index}>
                          {item.getIn(['product', 'item_part_num'], t('rma.noPartNumber', 'no part number'))} -{' '}
                          {item.getIn(['product', 'item_name'], t('rma.noProductName', 'no product name'))} -{' '}
                          {item.get('quantity_shipped', 0) > 0
                            ? t('rma.removedQty', 'Removed: {{qty}}', { qty: item.get('quantity_shipped', 0) })
                            : t('rma.addedQty', 'Added {{qty}}', { qty: item.get('quantity_added_to_inventory', 0) })}
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <InventoryQtySelect
                    title={t('rma.removeItemsFromInventoryCap', 'Remove Items From Inventory')}
                    fetchParams={{ used_in_rma: true }}
                    productFilter={p => p.used_in_rma === true}
                    value={removeFromInventory}
                    onQtyChange={setRemoveFromInventoryValue}
                    defaultOpen={true}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <InventoryQtySelect
                    title={t('rma.packingSupplies', 'Packing Supplies')}
                    fetchParams={{ used_in_packing_supplies: true }}
                    productFilter={p => p.used_in_packing_supplies === true}
                    value={packingSupplies}
                    onQtyChange={setPackingSuppliesValue}
                    qtyChoices={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                  />
                </Box>
              </SectionCard>

              {/* ── Notes ── */}
              <SectionCard title={t('rma.notes', 'Notes')}>
                <Field name="comments">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={4}
                      margin="normal"
                      label={t('rma.comments', 'Comments')}
                      inputProps={{ maxLength: 2048 }}
                      sx={multilineFilledSx}
                    />
                  )}
                </Field>
                <Field name="hidden_comments">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={4}
                      margin="normal"
                      label={t('rma.internalCommentsHidden', 'Internal Comments (hidden from customer)')}
                      inputProps={{ maxLength: 2048 }}
                      sx={multilineFilledSx}
                    />
                  )}
                </Field>
              </SectionCard>

              {/* ── Actions ── */}
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1, pb: 2 }}>
                <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
                  {t('general.cancel', 'Cancel')}
                </Button>
                <Button variant="contained" color="primary" disabled={!canSubmit || isSubmitting} onClick={submitForm}>
                  {t('general.submit', 'Submit')}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
}
